import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux"; // Or just useDispatch/useSelector
import { setToast } from "../../../../redux/actions/toast";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import { postDataWithDotNet } from "../../../../newApis/dotNetApiMethods";
import { errorHandler } from "../../../../helpers/errorHandler";
import { ErrorLoader } from "../../../../UI/Loaders/Loaders";
import { appInsights } from "../../../../config/appInsights";
import { formatMoney } from "../../../../helpers/formatter";
import {
  useAllCustomerWallet,
  WalletAccount,
} from "../../../../custom-hooks/useAllCustomerWallet";
import { WalletDetails } from "../../../../interfaces/individualCustomer";
import useGetMifoLoanDetails from "../../../../custom-hooks/useGetMifoLoanDetails";
import Loader from "../../../../NewComponents/Loader/Loader.component"; // Your custom hook

const RepayMode = [
  { name: "Wallet", value: 1 },
  { name: "Card", value: 2 },
  { name: "DirectDebit", value: 3 },
];

interface RepayLoanProps {
  dataRecord: any;
  onCloseModal: () => void;
}

const RepayLoans = ({ dataRecord, onCloseModal }: RepayLoanProps) => {
  const dispatch = useDispatch();

  const { core_banking_tenant } = dataRecord;

  const [formError, setFormError] = useState(false);
  const [state, setState] = useState({
    repayAmount: "",
    payMode: "",
    loan_ref: "",
    customer_wallet_id: "",
  });

  // UI States for loader/success/error messages
  const [showConfirm, setShowConfirm] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [size, setSize] = useState(20);
  const consumerType = "Customer";
  const [wallet, setWallet] = useState<WalletAccount[]>([]);

  const customerId = dataRecord?.borrower_details?.customer_id || undefined;

  const {
    data: allWalletsResponse,
    isLoading: allWalletIsLoading,
    error: allWalletError,
  } = useAllCustomerWallet({
    pageNumber,
    size,
    consumerType,
    customerId,
    Tenant: core_banking_tenant,
  });

  useEffect(() => {
    setWallet(
      allWalletsResponse?.data?.filter(
        (wallet: WalletDetails) =>
          wallet.walletProvider.toLowerCase() === "advanclymfb",
      ),
    );
  }, [allWalletsResponse, allWalletIsLoading]);

  const {
    data: mifosLoanDetails,
    isLoading: mifosLoanDetailsIsLoading,
    error: mifosLoanDetailsError,
  } = useGetMifoLoanDetails({
    loan_ref: dataRecord?.loan_ref,
  });

  const handleOptionSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const repayLoan = async (e: React.FormEvent) => {
    e.preventDefault();
    const { loan_ref } = dataRecord;
    if (state?.payMode && state.repayAmount) {
      const params = {
        amount: Number(state?.repayAmount),
        loan_ref_no: loan_ref,
        repayment_mode: Number(state?.payMode),
      };

      setShowConfirm(false);
      setShowLoader(true);
      setShowSuccess(false);
      setShowErrorModal(false);

      try {
        const result = await postDataWithDotNet(
          ajaxEndpoints.REPAYMENTS_LOAN,
          params,
        );

        setShowLoader(false);
        setShowSuccess(true);
        setTimeout(() => {
          dispatch(setToast("success", result.message));
          onCloseModal();
        }, 3000);
      } catch (error: any) {
        appInsights.trackException({
          exception: error,
          properties: {
            fileName: "RepayLoans.js (Functional)",
          },
        });
        setShowLoader(false);
        setShowErrorModal(true);
        setTimeout(() => {
          dispatch(setToast("warning", errorHandler(error)));
          onCloseModal();
        }, 3000);
      }
    } else {
      setFormError(true);
    }
  };

  const { borrower_details, loan_id, loan_ref, borrowerName } = dataRecord;

  return (
    <div className="Modal__dialog lg-modal animated slideInDown">
      <div className="IssueModal Modal__content" style={{ overflow: "hidden" }}>
        <div className="Modal__body" style={{ overflow: "hidden" }}>
          {mifosLoanDetailsIsLoading ? (
            <div style={{ textAlign: "center" }}>
              <Loader />
            </div>
          ) : (
            <>
              <h3 className="Modal__title p-b-1">Repayment Summary</h3>

              {/* 1. Confirm Repayment */}
              {showConfirm && (
                <div id="disbursement-confirm">
                  <div className="m-b-30">
                    <h5>Are you sure you want to repay the following loan? </h5>
                    <p className="mt-3">
                      Borrower Name:{" "}
                      {borrower_details?.first_name
                        ? `${borrower_details?.first_name} ${borrower_details?.last_name}`
                        : borrowerName}
                    </p>
                    {wallet?.length > 0 ? (
                      <p>
                        Wallet Balance:
                        {formatMoney(wallet?.[0]?.availableBalance)}
                      </p>
                    ) : null}
                    <p>
                      Loan Repay Amount:{" "}
                      {formatMoney(
                        mifosLoanDetails?.data?.responseData?.summary
                          ?.principalOutstanding,
                      )}
                    </p>
                    <form onSubmit={repayLoan}>
                      {formError && (
                        <p className="text-danger">
                          Repayment Amount and Payment Mode cannot be blank.
                        </p>
                      )}
                      <div className="row">
                        <div className="col-md-6">
                          <label style={{ width: "100%" }}>
                            Repayment Amount
                            <input
                              className="form-control"
                              name="repayAmount"
                              value={state.repayAmount}
                              onChange={handleOptionSelect}
                              type="number"
                              required
                            />
                          </label>
                        </div>
                        <div className="col-md-6">
                          <label style={{ width: "100%" }}>
                            Payment Mode
                            <select
                              name="payMode"
                              className="form-control"
                              onChange={handleOptionSelect}
                              value={state?.payMode}
                              required
                            >
                              <option value="">Select</option>
                              {RepayMode.map((mode) => (
                                <option key={mode.value} value={mode.value}>
                                  {mode.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <label style={{ width: "100%" }}>
                            Loan Reference
                            <input
                              className="form-control"
                              name="loan_ref"
                              value={loan_ref}
                              readOnly
                            />
                          </label>
                        </div>
                        {wallet?.length > 0 ? (
                          <div className="col-md-6">
                            <label style={{ width: "100%" }}>
                              Select Account
                              <select
                                className="form-control"
                                style={{ width: "100%" }}
                                name="customer_wallet_id"
                                value={state?.customer_wallet_id}
                                onChange={handleOptionSelect}
                                disabled={
                                  allWalletIsLoading ||
                                  !allWalletsResponse?.data?.length
                                }
                              >
                                <option value="">
                                  {allWalletIsLoading
                                    ? "Loading wallets..."
                                    : "Select an account"}
                                </option>
                                {allWalletError && (
                                  <option disabled value="">
                                    Error loading wallets
                                  </option>
                                )}
                                {allWalletsResponse &&
                                  allWalletsResponse.data?.map(
                                    (wallet: WalletDetails) => (
                                      <option
                                        key={wallet?.id}
                                        value={wallet?.id}
                                      >
                                        {wallet?.accountNumber} |{" "}
                                        {wallet?.accountName} |{" "}
                                        {wallet?.bankName}
                                      </option>
                                    ),
                                  )}
                              </select>
                            </label>
                          </div>
                        ) : null}
                      </div>

                      <div className="Form__action-buttons m-t-2">
                        <button
                          type="button"
                          onClick={onCloseModal}
                          className="m-0 mr-auto btn zoom-element login-btn"
                        >
                          <i className="fa fa-times-circle" /> Cancel
                        </button>
                        <button
                          type="submit"
                          data-id={loan_id}
                          disabled={
                            !(state?.payMode && state.repayAmount) ||
                            showLoader ||
                            (state?.payMode === "1" &&
                              !state.customer_wallet_id &&
                              allWalletsResponse?.data?.length < 0)
                          }
                          className="m-0 ml-auto btn zoom-element login-btn"
                        >
                          <i className="fa fa-check-circle" /> Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {showLoader && (
                <div
                  id="disbursement-loader"
                  className="loading-overlay speed-1x fadeInRight"
                >
                  <div className=" form-loader form-loader-login verify">
                    <div className="centerLoader">
                      <div className="loading" id="adv-loader-alt" />
                      <div className="center-txt webapp-ripple-text">
                        <i className="notifier fas fa-user-cog" />
                        Processing Repayment ...
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showSuccess && (
                <div
                  id="disbursement-success"
                  className="loading-overlay speed-1x fadeInRight"
                >
                  <div className="form-loader form-loader-login verify">
                    <div className="centerLoader">
                      <div className="loading success-loader">
                        <i className="webapp-ripple-notification success-text fa fa-check-circle" />
                      </div>
                      <div className="center-txt webapp-ripple-text">
                        <i className="notifier far fa-check-circle" />
                        Repayment Successful
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showErrorModal && (
                <div
                  id="disbursement-error"
                  className="loading-overlay speed-1x fadeInRight"
                >
                  <ErrorLoader
                    icon="fas fa-wifi"
                    text="Network Issue"
                    subText="Unable to connect to Advancly services."
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepayLoans;
